import { Body } from "@sprout/typography";
import { Link } from "@inertiajs/react";
import EditIcon from "@sprout/icons/edit";
import "./entry_point.scss";
import useTranslation from "@hooks/use_translation";
import CompletionTag from "@components/completion_tag.tsx";
import { CompletionStatus, CompletionStatusType } from "@utils/completion_status.ts";
import ClearVerifiedBadge from "@/components/ui/clear_verified_badge";
import useDevice from "@hooks/use_device.ts";
import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import { Tag } from "@sprout/tag";
import theme from "@/stylesheets/theme.module.scss";
import { SecondaryButton, SecondaryButtonLink } from "@sprout/buttons";

type Props = {
  href: string;
  sectionHeader: string;
  fullName?: string;
  email?: string | null;
  phoneNumber?: string | null;
  location?: string;
  description?: string;
  workPreferences?: string[];
  showDescription?: boolean;
  completionStatus: CompletionStatusType;
  showClearActionRequired?: boolean;
  isClearVerified?: boolean;
};

const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.startsWith("+") && intlTelInput.utils) {
    return intlTelInput.utils.formatNumber(
      phoneNumber,
      undefined,
      intlTelInput.utils.numberFormat.INTERNATIONAL,
    );
  }

  return phoneNumber;
};

const EntryPoint = (props: Props) => {
  const {
    href,
    sectionHeader,
    fullName,
    email,
    phoneNumber,
    location,
    completionStatus,
    description,
    workPreferences = [],
    showDescription,
    showClearActionRequired = false,
    isClearVerified = false,
  } = props;
  const { t } = useTranslation("user");
  const { t: tClear } = useTranslation("clear_verification");
  const device = useDevice();

  const hasFullName = Boolean(fullName && fullName.trim());
  const actionRequiredLabel = tClear("badge.action_required");

  const savedWorkPreferences = workPreferences?.map((preference, index) => (
    <Body key={index} light>
      {preference}
    </Body>
  ));

  return (
    <div className="entry-point">
      <Link href={href} className="entry-point__header">
        <Body medium>{sectionHeader}</Body>
        <CompletionTag status={completionStatus} />
        {completionStatus === CompletionStatus.NotStarted ? (
          <>{device.isWeb && <SecondaryButton size="medium">{t("getStarted")}</SecondaryButton>}</>
        ) : (
          <div className="entry-point__header__icon">
            <EditIcon />
          </div>
        )}
      </Link>
      {hasFullName && (
        <div className="flex items-center gap-2">
          <Body medium>{fullName}</Body>
          {isClearVerified && <ClearVerifiedBadge />}
        </div>
      )}
      {email && (
        <div className="flex items-center gap-2 min-w-0">
          <Body light className="truncate" title={email}>
            {email}
          </Body>
          {!hasFullName && isClearVerified && <ClearVerifiedBadge />}
        </div>
      )}
      {showClearActionRequired && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <ClearVerifiedBadge tooltip={actionRequiredLabel} />
            <span className="text-xs font-medium uppercase tracking-wide">{tClear("brand")}</span>
          </div>
          <Tag label={actionRequiredLabel} color={theme.irisE50} />
        </div>
      )}
      {phoneNumber && <Body light>{formatPhoneNumber(phoneNumber)}</Body>}
      {location && <Body light>{location}</Body>}
      {workPreferences.length > 0 && savedWorkPreferences}
      <div className={`entry-point__description ${device.isMobile && "items-center"}`}>
        {showDescription && description && <Body light>{description}</Body>}
        {device.isMobile && completionStatus === CompletionStatus.NotStarted && (
          <SecondaryButtonLink href={href} size="medium">
            {t("getStarted")}
          </SecondaryButtonLink>
        )}
      </div>
    </div>
  );
};

export default EntryPoint;
